import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { useState } from "react";
import { MilestoneDialog } from "./MilestoneDialog";
import { EditableTable, EditableTableColumn } from "@/components/ui/editable-table";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface Milestone {
  id: number;
  description: string;
  amount: number;
  currency: string;
  due_date: string | null;
  status: 'pending' | 'active' | 'delivered' | 'contested' | 'approved' | 'invoiced' | 'paid';
}

interface ProjectMilestonesProps {
  projectId: number;
  milestones: Milestone[];
  projectType: string;
}

const milestoneStatusOptions = [
  { label: 'Pending', value: 'pending' },
  { label: 'Active', value: 'active' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Contested', value: 'contested' },
  { label: 'Approved', value: 'approved' },
  { label: 'Invoiced', value: 'invoiced' },
  { label: 'Paid', value: 'paid' }
];

export function ProjectMilestones({ projectId, milestones, projectType }: ProjectMilestonesProps) {
  const [isMilestoneDialogOpen, setIsMilestoneDialogOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  if (projectType !== 'fixed_price') {
    return null;
  }

  const totalMilestonesAmount = milestones?.reduce((sum, milestone) => 
    sum + milestone.amount, 0
  ) || 0;

  const handleUpdate = async (id: number, field: keyof Milestone, value: string) => {
    try {
      let processedValue = value;
      
      // Handle special cases for different field types
      if (field === 'amount') {
        processedValue = parseFloat(value).toString();
      } else if (field === 'due_date') {
        processedValue = value || null;
      }

      const { error } = await supabase
        .from('milestones')
        .update({ [field]: processedValue })
        .eq('id', id);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });

      toast({
        title: "Success",
        description: "Milestone updated successfully",
      });
    } catch (error) {
      console.error('Error updating milestone:', error);
      toast({
        title: "Error",
        description: "Failed to update milestone",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const { error } = await supabase
        .from('milestones')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['project', projectId] });

      toast({
        title: "Success",
        description: "Milestone deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete milestone",
        variant: "destructive",
      });
    }
  };

  // Sort milestones by due date, with null dates at the end
  const sortedMilestones = [...(milestones || [])].sort((a, b) => {
    if (!a.due_date && !b.due_date) return 0;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
  });

  const columns: EditableTableColumn<Milestone>[] = [
    { key: 'description', header: 'Description', type: 'text' },
    { 
      key: 'amount', 
      header: `Amount (${milestones?.[0]?.currency || 'USD'})`, 
      type: 'text'
    },
    { key: 'due_date', header: 'Due Date', type: 'text' },
    { 
      key: 'status', 
      header: 'Status', 
      type: 'select',
      options: milestoneStatusOptions
    }
  ];

  return (
    <Card className="md:col-span-2">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          Milestones
        </CardTitle>
        <div className="flex items-center gap-4">
          <p className="text-sm text-muted-foreground">
            Total: {totalMilestonesAmount} {milestones?.[0]?.currency || 'USD'}
          </p>
          <Button size="sm" onClick={() => setIsMilestoneDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Milestone
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <EditableTable
          data={sortedMilestones}
          columns={columns}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      </CardContent>

      <MilestoneDialog 
        open={isMilestoneDialogOpen}
        onOpenChange={setIsMilestoneDialogOpen}
        projectId={projectId}
        projectCurrency={milestones?.[0]?.currency}
      />
    </Card>
  );
}